import { Input, Button, Popover, Form, message } from 'antd'
import { isFinite, isEmpty } from 'lodash'
import produce from 'immer'
import cx from 'clsx'
import { createChildListViewComponent } from 'factories/ChildListView'
import entityNames from 'options/entityNames'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { DARK_GREEN } from 'options/colors'
import { getUserPermission } from 'helpers/auth'
import { validateQuantityRequisitioned, validateQuantityIssued } from 'helpers/procurement'
import { t } from 'helpers/i18n'
import { isReadOnly } from 'helpers/formViews'
import { DEBOUNCE } from 'helpers/utils'
import Icon from 'elements/Icon'
import FormView from 'containers/Requisitions/Items/FormView'
import InputNumber from 'elements/InputNumber'
import ConvertToOrder from 'containers/Requisitions/Items/ConvertToOrder'
import ConvertToRfq from 'containers/Requisitions/Items/ConvertToRfq'
import PickList from 'containers/Requisitions/PickList'
import Copy from 'containers/Requisitions/Items/Copy'
import Modal from 'elements/Modal'

export default createChildListViewComponent({
  entityName: entityNames.requisitions,
  childName: 'requisitionItems',
  createButtonTextId: 'addNonStockItem',
  createButtonLinkTarget: {
    formSize: 'md',
    formComponent: (props) => <FormView {...props} settingsType="requisition" />,
  },
  primaryLinkTarget: 'requisitionItemRecord',
  allowCreate: (self) => false,
  allowDelete: (self) => !isReadOnly(self) && getUserPermission('Purchase') !== 'No',
  allowCustomize: (self) => true,
  allowSearching: (self) => true,
  extraRowActions: (self) => (
    <>
      {!self.props.userIsInRoleDistributor &&
        !self.props.userIsInRoleSupplier &&
        getUserPermission('Purchase') !== 'No' && (
          <Copy
            parentRecord={self.props.parentRecord}
            parentHasUnsavedChanges={self.props.parentHasUnsavedChanges}
            selectedRowKeys={self.state.selectedRowKeys}
          />
        )}
      {(!isReadOnly(self) || self.props.parentRecord.status === 'Closed') && getUserPermission('Orders') !== 'None' && (
        <Button
          onClick={() =>
            self.props.parentHasUnsavedChanges
              ? message.error(t('saveChangesFirst'))
              : !self.props.parentRecord.approved
                ? message.error(t('requisitionApprovalRequired'))
                : self.setState({ convertToOrderVisible: true })
          }
          disabled={isEmpty(self.state.selectedRowKeys)}
        >
          <Icon type="ShoppingCart" />
          {t('convertToOrder')}
        </Button>
      )}
      {(!isReadOnly(self) || self.props.parentRecord.status === 'Closed') && getUserPermission('RFQs') !== 'None' && (
        <Button
          onClick={() =>
            self.props.parentHasUnsavedChanges
              ? message.error(t('saveChangesFirst'))
              : !self.props.parentRecord.approved
                ? message.error(t('requisitionApprovalRequired'))
                : self.setState({ convertToRfqVisible: true })
          }
          disabled={isEmpty(self.state.selectedRowKeys)}
        >
          <Icon type="AttachMoney" />
          {t('convertToRfq')}
        </Button>
      )}
      {['Draft', 'Open', 'Closed'].includes(self.props.parentRecord.status) && self.props.parentRecord.id && (
        <Button
          onClick={() =>
            self.props.parentHasUnsavedChanges
              ? message.error(t('saveChangesFirst'))
              : self.setState({ pickListVisible: true })
          }
          disabled={isEmpty(self.state.selectedRowKeys)}
        >
          <Icon type="ContentPaste" />
          {t('pickList')}
        </Button>
      )}
      {self.state.convertToOrderVisible && (
        <ConvertToOrder
          parentRecord={self.props.parentRecord}
          selectedRowKeys={self.state.selectedRowKeys}
          onCancel={() => self.setState({ convertToOrderVisible: false })}
          onCreateOrder={self.props.onCreateOrder}
          onCreatedOrderDrawerClose={() =>
            self.setState({ convertToOrderVisible: false }, self.props.onCreatedOrderDrawerClose)
          }
        />
      )}
      {self.state.convertToRfqVisible && (
        <ConvertToRfq
          parentRecord={self.props.parentRecord}
          selectedRowKeys={self.state.selectedRowKeys}
          onCancel={() => self.setState({ convertToRfqVisible: false })}
          onCreateRfq={self.props.onCreateRfq}
          onCreatedRfqDrawerClose={() =>
            self.setState({ convertToRfqVisible: false }, self.props.onCreatedRfqDrawerClose)
          }
        />
      )}
      <Modal
        title={t('printPreview')}
        okText={t('print')}
        onOk={createPrintHandler(self.printableRef)}
        onCancel={() => self.setState({ pickListVisible: false }, resetPrintHandler)}
        width={992}
        visible={self.state.pickListVisible}
      >
        <div ref={self.printableRef} className="tofino-print-preview">
          <PickList parentRecord={self.props.parentRecord} selectedRowKeys={self.state.selectedRowKeys} />
        </div>
      </Modal>
    </>
  ),
  tableCellComponents: {
    quantity: (self, item) => (
      <div className="whitespace-nowrap pr-12">
        <InputNumber
          value={item.quantity}
          onChange={async (value) => {
            try {
              if (!self.props.customer.generalSettings.allowOverIssueFromRequisition) {
                validateQuantityRequisitioned(item.quantityIssued, Math.max(value, 1))
              }
              await self.props.onChange(
                produce(self.props.items, (draft) => {
                  if (isFinite(value)) {
                    const row = draft.find((one) => one.id === item.id)
                    row.quantity = Math.max(value, 1)
                    row.totalCost = row.quantity * row.price
                    row.populated = true // HACK: To avoid calling /populate
                  }
                })
              )
            } catch (error) {
              console.warn(error)
            }
          }}
          min={1}
          readOnly={isReadOnly(self)}
        />{' '}
        <Popover title={t('itemStatus')} content={<span dangerouslySetInnerHTML={{ __html: item.itemStatusInfo }} />}>
          <span className="align-middle" style={{ visibility: isEmpty(item.itemStatusInfo) ? 'hidden' : 'visible' }}>
            <Icon type="Search" />
          </span>
        </Popover>
      </div>
    ),
    quantityIssued: (self, item) => {
      const quantityIssuedEnabled =
        self.props.parentRecord?.approved === true &&
        ['Open', 'Closed'].includes(self.props.parentRecord?.status) &&
        getUserPermission('Issue') === 'Yes'

      return (
        <div className="whitespace-nowrap pr-12">
          <InputNumber
            value={item.quantityIssued}
            onChange={async (value) => {
              try {
                if (!self.props.customer.generalSettings.allowOverIssueFromRequisition) {
                  await validateQuantityIssued(item.quantity, value)
                }
                await self.props.onChange(
                  produce(self.props.items, (draft) => {
                    if (isFinite(value)) {
                      const row = draft.find((one) => one.id === item.id)
                      row.quantityIssued = value
                      row.populated = false
                    }
                  })
                )
              } catch (error) {
                console.warn(error)
              }
            }}
            className={cx({
              'form-field-success':
                item.quantityIssued === item.quantity ||
                (self.props.customer.generalSettings.allowOverIssueFromRequisition &&
                  item.quantityIssued > item.quantity),
              'form-field-error':
                !self.props.customer.generalSettings.allowOverIssueFromRequisition &&
                item.quantityIssued > item.quantity,
            })}
            readOnly={isReadOnly(self)}
            disabled={!quantityIssuedEnabled}
            min={0}
            debounce={DEBOUNCE}
          />{' '}
          <Popover
            title={t('issueStatus')}
            content={<span dangerouslySetInnerHTML={{ __html: item.issueStatusInfo }} />}
          >
            <span className="align-middle" style={{ visibility: isEmpty(item.issueStatusInfo) ? 'hidden' : 'visible' }}>
              <Icon type="Search" />
            </span>
          </Popover>
        </div>
      )
    },
    itemOrdered: (self, item) => (item.itemOrdered ? <Icon type="Done" color={DARK_GREEN} size={20} /> : null),
    price: (self, item) => (
      <InputNumber
        value={item.price}
        onChange={async (value) => {
          try {
            await self.props.onChange(
              produce(self.props.items, (draft) => {
                if (isFinite(value)) {
                  const row = draft.find((one) => one.id === item.id)
                  row.price = value
                  row.totalCost = row.quantity * row.price
                  row.populated = true // HACK: To avoid calling /populate
                }
              })
            )
          } catch (error) {
            console.warn(error)
          }
        }}
        min={0}
        readOnly={isReadOnly(self)}
        currency
      />
    ),
    description: (self, item) => (
      <Form.Item
        style={{ paddingBottom: 0, marginBottom: 0 }}
        validateStatus={isEmpty(item.description) ? 'error' : undefined}
        help={isEmpty(item.description) ? t('requiredField') : undefined}
      >
        <Input.TextArea
          rows={1}
          value={item.description}
          onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
          onChange={async (e) => {
            try {
              const { value } = e.target
              await self.props.onChange(
                produce(self.props.items, (draft) => {
                  const row = draft.find((one) => one.id === item.id)
                  row.description = value
                  row.populated = true // HACK: To avoid calling /populate
                })
              )
            } catch (error) {
              console.warn(error)
            }
          }}
          readOnly={isReadOnly(self)}
          autoSize
        />
      </Form.Item>
    ),
  },
})()
